import * as React from "react";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Trash2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import { FilterCombobox, FilterComboboxOption } from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import ActionConfirmationModal, { ACTION_CONFIGS } from "../common/ActionConfirmationModal";
import { compareBy, Order, stableSort } from "../../helpers/TableHelpers";
import JobsRepository from "../../repositories/JobsRepository";
import { ExportFlaggedPathsToCsvRequest, JobRequest } from "../../models/Jobs/JobRequest";
import {
  JobRequestStatus,
  JobRequestStatusColors,
  JobRequestStatusIcons,
  JobRequestStatusLabels,
} from "../../models/Jobs/JobRequestStatus";
import { UserRole } from "../../models/User";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { QUERY_CONFIG } from "../../constants/QueryConfig";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface ExportFlaggedPathsToCsvStatusTableProps {
  userRole: UserRole;
  isAssignedUser: boolean;
  organizationId: string;
}

const statusFilterOptions: FilterComboboxOption[] = Object.values(JobRequestStatus)
  .filter((status) => status !== JobRequestStatus.New)
  .map((status) => ({
    value: status,
    label: JobRequestStatusLabels[status],
    color: JobRequestStatusColors[status],
    icon: JobRequestStatusIcons[status],
  }));

export const ExportFlaggedPathsToCsvStatusTable: React.FC<ExportFlaggedPathsToCsvStatusTableProps> = ({
  userRole,
  isAssignedUser,
  organizationId,
}: ExportFlaggedPathsToCsvStatusTableProps) => {
  const requiresReason = useRequiresReason();
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const [order, setOrder] = React.useState<Order>("desc");
  const [sort, setSort] = React.useState<keyof ExportFlaggedPathsToCsvRequest>("exportName");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [exportFlaggedPathsStatus, setExportFlaggedPathsStatus] = useState<ExportFlaggedPathsToCsvRequest[] | null>(null);

  const exportsQuery = useQuery({
    queryKey: [QUERY_KEYS.EXPORT_FLAGGED_PATHS, organizationId],
    queryFn: async () => await jobsRepository.getFlaggedPathCsvGenerationStatus(organizationId),
    staleTime: QUERY_CONFIG.DEFAULT_STALE_TIME,
    refetchInterval: QUERY_CONFIG.DEFAULT_REFETCH_INTERVAL,
    refetchIntervalInBackground: true,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (exportsQuery.data)
      setExportFlaggedPathsStatus(exportsQuery.data);
  }, [exportsQuery]);

  const handleDownload = (url: string | undefined) => {
    if (url)
      window.open(url, "_blank");
  };

  const changeSort = (newSort: keyof ExportFlaggedPathsToCsvRequest) => setSort(newSort);
  const changeOrder = (newOrder: Order) => setOrder(newOrder);

  const filteredJobs = (exportFlaggedPathsStatus || [])
    .filter((job) => statusFilter == null || job.status === statusFilter);

  const sortedJobs = sort
    ? stableSort<ExportFlaggedPathsToCsvRequest>(filteredJobs, compareBy(order, sort))
    : filteredJobs;

  return (
    <React.Fragment>
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerFilterItemClassName}>
          <Label className="text-sm">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={(newValue) => setStatusFilter(newValue)}
            options={statusFilterOptions}
            placeholder="Status"
          />
        </div>
      </div>

      <div className={drawerTableWrapperClassName}>
        {exportsQuery.isFetching || exportsQuery.isError ? (
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <Table className="table-fixed" containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[15%] min-w-[48px] text-center p-3" />
                <TableHead className="w-[40%] p-3">
                  <SortableTableColumnHeader
                    label="Name"
                    sortProperty="exportName"
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort as keyof ExportFlaggedPathsToCsvRequest)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[30%] p-3">
                  <SortableTableColumnHeader
                    label="Status"
                    sortProperty="status"
                    getSort={() => sort}
                    setSort={(newSort) => changeSort(newSort as keyof JobRequest)}
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[15%] p-3" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedJobs.map((exportCsvJob) => {
                const canEdit =
                  exportCsvJob.status === JobRequestStatus.Pending ||
                  exportCsvJob.status === JobRequestStatus.Completed;
                return (
                  <TableRow key={exportCsvJob.id}>
                    <TableCell className="text-center">
                      {requiresReason ? (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.EXPORT_FLAGGED_PATH}
                          onConfirm={async (reason) => {
                            await jobsRepository.auditLogFlaggedPathsCsvExportDownload(exportCsvJob.id, reason);
                            handleDownload(exportCsvJob.exportedCsvUrl);
                          }}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Download CSV"
                            disabled={!exportCsvJob.exportedCsvUrl}
                          >
                            <Download className="size-4" />
                          </Button>
                        </ActionConfirmationModal>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Download CSV"
                          onClick={() => handleDownload(exportCsvJob.exportedCsvUrl)}
                          disabled={!exportCsvJob.exportedCsvUrl}
                        >
                          <Download className="size-4" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate block">{exportCsvJob.exportName}</span>
                    </TableCell>
                    <TableCell>
                      <JobRequestStatusChip status={exportCsvJob.status} />
                    </TableCell>
                    <TableCell>
                      {canEdit && (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          aria-label="Cancel"
                          onClick={() => setAbortJob(exportCsvJob)}
                          disabled={!isAssignedUser && userRole !== UserRole.Admin}
                        >
                          {exportCsvJob.status === JobRequestStatus.Completed ? (
                            <Trash2 className="size-4" />
                          ) : (
                            <XCircle className="size-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {sortedJobs.length === 0 && !exportsQuery.isFetching && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No exports.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {abortJob && (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => setAbortJob(null)}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_FLAGGED_PATHS, organizationId] });
            setAbortJob(null);
          }}
        />
      )}
    </React.Fragment>
  );
};
